/**
 * webhotell-content.ts — innhold for /webhotell (norsk + engelsk)
 *
 * ⚠ PLASSHOLDERE: pakkenavn, spesifikasjoner, priser, spørsmål/svar og
 * kontaktadresse er foreløpige og MÅ erstattes med det som faktisk leveres
 * før siden publiseres. All redigering gjøres her — komponentene har ingen
 * egne tekster eller tall.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Når prisene skal
 * styres fra systemet, erstattes denne filen av et API-kall (typen
 * WebhotellContent i src/types/webhotell.ts er kontrakten).
 */

import type { WebhotellContent } from '@/types/webhotell';
import type { Locale } from '@/types/landing';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
const CONTACT = 'mailto:admin@ntune.no?subject=inHost';

const no: WebhotellContent = {
  seoTitle: 'inHost — webhotell fra inSuite',
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }],
    current: 'inHost',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'pakker', label: 'Pakker' },
      { id: 'sammenlign', label: 'Sammenlign' },
      { id: 'bruk', label: 'Bruksområder' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se pakkene', href: '#pakker' },
  },
  hero: {
    heading: 'inHost',
    subheading: 'Webhotell med drift, sikkerhet og support for nettsider, e-post og databaser, i pakker du kan bytte mellom når behovet vokser.',
    primaryCta: { label: 'Se pakker og priser', href: '#pakker' },
    secondaryCta: { label: 'Dette får du', href: '#funksjoner' },
    illustrationLabel: 'Illustrasjon av servere, sky og sikkerhet',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Nettstedet ditt. Vår drift.',
    body: [
      'inHost er webhotellet fra inSuite: du får plassen nettsidene, e-posten og databasene dine trenger, uten at du må sette opp og vedlikeholde en server selv.',
      'Du velger en pakke etter behov. Vi står for drift, sikkerhetsoppdateringer og backup, slik at du kan bruke tiden på innholdet.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Hver pakke inneholder det du trenger for å komme i gang.',
    panelLabel: 'inHost',
    items: [
      { title: 'Rask lagring', body: 'Nettsidene dine ligger på SSD-lagring, slik at sidene lastes raskt.' },
      { title: 'Sikkerhet', body: 'SSL-sertifikat følger med alle pakker, og serverne holdes oppdatert.' },
      { title: 'Backup', body: 'Sikkerhetskopi av nettsider og databaser, med daglig backup i de større pakkene.' },
      { title: 'E-post på ditt domene', body: 'Opprett e-postkontoer på ditt eget domene.' },
      { title: 'Databaser', body: 'Databaser til CMS og webapplikasjoner, med antall etter pakke.' },
      { title: 'Support', body: 'Hjelp når du står fast, med prioritert support i den største pakken.' },
    ],
  },
  promo: {
    title: 'Usikker på hvilken pakke du trenger?',
    body: 'Fortell oss hva nettstedet skal brukes til, så anbefaler vi en pakke.',
    cta: { label: 'Kontakt oss', href: CONTACT },
  },
  plans: {
    eyebrow: 'Pakker',
    heading: 'Pakker og priser',
    intro: 'Velg pakken som passer, og bytt til en større når behovet vokser.',
    cardsHeading: 'Velg pakke',
    fromLabel: 'Fra',
    footnote: '*Prisene er veiledende, per måned og eksklusiv mva.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For en enkel nettside.',
        price: '49',
        priceNote: 'kr / mnd',
        features: ['1 domene', '10 GB SSD-lagring', '5 e-postkontoer', '1 database', 'SSL-sertifikat'],
        cta: { label: 'Bestill Start', href: CONTACT },
      },
      {
        id: 'pluss',
        name: 'Pluss',
        summary: 'For bedrifter med flere nettsteder.',
        price: '149',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Start, pluss',
        features: ['5 domener', '50 GB SSD-lagring', '25 e-postkontoer', '10 databaser', 'Daglig backup'],
        cta: { label: 'Bestill Pluss', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Anbefalt',
      },
      {
        id: 'pro',
        name: 'Pro',
        summary: 'For større og mer krevende nettsteder.',
        price: '349',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Pluss, pluss',
        features: ['25 domener', '200 GB SSD-lagring', '100 e-postkontoer', '50 databaser', 'Prioritert support'],
        cta: { label: 'Bestill Pro', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Sammenlign',
    heading: 'Sammenlign pakkene',
    intro: 'Se hva hver pakke inneholder.',
    featureLabel: 'Funksjon',
    yes: 'Inkludert',
    no: 'Ikke inkludert',
    rows: [
      { label: 'Kapasitet', values: [], group: true },
      { label: 'Domener', values: ['1', '5', '25'] },
      { label: 'SSD-lagring', values: ['10 GB', '50 GB', '200 GB'] },
      { label: 'E-postkontoer', values: ['5', '25', '100'] },
      { label: 'Databaser', values: ['1', '10', '50'] },
      { label: 'Sikkerhet og support', values: [], group: true },
      { label: 'SSL-sertifikat', values: [true, true, true] },
      { label: 'Daglig backup', values: [false, true, true] },
      { label: 'Prioritert support', values: [false, false, true] },
    ],
  },
  useCases: {
    eyebrow: 'Bruksområder',
    heading: 'Slik brukes det',
    items: [
      { icon: 'domain', title: 'Nettside for bedriften', body: 'Publiser en nettside på ditt eget domene, med e-post og SSL.', link: { label: 'Be om tilbud', href: CONTACT } },
      { icon: 'database', title: 'CMS og webapplikasjoner', body: 'Kjør nettsted eller applikasjon med egne databaser.', link: { label: 'Be om tilbud', href: CONTACT } },
      { icon: 'backup', title: 'Flytte fra annen leverandør', body: 'Vi hjelper deg med å flytte nettsider, databaser og e-post.', link: { label: 'Kontakt oss', href: CONTACT } },
    ],
  },
  related: {
    eyebrow: 'Relatert',
    heading: 'Relaterte tjenester',
    items: [
      { icon: 'wordpress', name: 'inWordPress — WordPress-hosting', href: '/wordpress' },
      { icon: 'nodejs', name: 'inNode — Node.js-hosting', href: '/nodejs' },
      { icon: 'virtual', name: 'inVPS — virtuelle servere', href: '/vps' },
      { icon: 'globe', name: 'inDomain — domener', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      { question: 'Kan jeg bytte pakke senere?', answer: 'Ja, du kan flytte til en større pakke når behovet vokser. Ta kontakt, så hjelper vi deg.' },
      { question: 'Kan jeg flytte nettstedet mitt hit?', answer: 'Ja. Vi hjelper deg med å flytte nettsider, databaser og e-post fra din nåværende leverandør.' },
      { question: 'Følger det med SSL-sertifikat?', answer: 'Ja, SSL-sertifikat er inkludert i alle pakker.' },
      { question: 'Hvordan bestiller jeg?', answer: 'Send oss en e-post med pakken du ønsker, så tar vi kontakt for å sette opp webhotellet ditt.' },
    ],
  },
  cta: {
    heading: 'Klar for å komme i gang?',
    body: 'Kontakt oss, så setter vi opp webhotellet ditt.',
    primary: { label: 'Kontakt oss', href: CONTACT },
  },
  footer: {
    tagline: 'inHost — webhotell med drift, sikkerhet og support for nettstedet ditt.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inHost',
  },
};

const en: WebhotellContent = {
  seoTitle: 'inHost — web hosting from inSuite',
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }],
    current: 'inHost',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'pakker', label: 'Plans' },
      { id: 'sammenlign', label: 'Compare' },
      { id: 'bruk', label: 'Use cases' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the plans', href: '#pakker' },
  },
  hero: {
    heading: 'inHost',
    subheading: 'Web hosting with operations, security and support for websites, email and databases, in plans you can move between as your needs grow.',
    primaryCta: { label: 'See plans and pricing', href: '#pakker' },
    secondaryCta: { label: 'What you get', href: '#funksjoner' },
    illustrationLabel: 'Illustration of servers, cloud and security',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'Your website. Our operations.',
    body: [
      'inHost is web hosting from inSuite: it gives your websites, email and databases the room they need, without you setting up and maintaining a server yourself.',
      'You choose a plan to fit your needs. We handle operations, security updates and backup, so you can spend your time on the content.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'Every plan includes what you need to get started.',
    panelLabel: 'inHost',
    items: [
      { title: 'Fast storage', body: 'Your websites run on SSD storage, so pages load quickly.' },
      { title: 'Security', body: 'An SSL certificate comes with every plan, and the servers are kept up to date.' },
      { title: 'Backup', body: 'Backups of websites and databases, with daily backup in the larger plans.' },
      { title: 'Email on your domain', body: 'Create email accounts on your own domain.' },
      { title: 'Databases', body: 'Databases for your CMS and web applications, with the number set by the plan.' },
      { title: 'Support', body: 'Help when you get stuck, with priority support in the largest plan.' },
    ],
  },
  promo: {
    title: 'Not sure which plan you need?',
    body: 'Tell us what the website will be used for, and we will recommend a plan.',
    cta: { label: 'Contact us', href: CONTACT },
  },
  plans: {
    eyebrow: 'Plans',
    heading: 'Plans and pricing',
    intro: 'Pick the plan that fits, and move up to a larger one as your needs grow.',
    cardsHeading: 'Choose a plan',
    fromLabel: 'From',
    footnote: '*Prices are indicative, per month and exclude VAT.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For a simple website.',
        price: '49',
        priceNote: 'NOK / month',
        features: ['1 domain', '10 GB SSD storage', '5 email accounts', '1 database', 'SSL certificate'],
        cta: { label: 'Order Start', href: CONTACT },
      },
      {
        id: 'pluss',
        name: 'Plus',
        summary: 'For businesses with several websites.',
        price: '149',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Start, plus',
        features: ['5 domains', '50 GB SSD storage', '25 email accounts', '10 databases', 'Daily backup'],
        cta: { label: 'Order Plus', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Recommended',
      },
      {
        id: 'pro',
        name: 'Pro',
        summary: 'For larger and more demanding websites.',
        price: '349',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Plus, plus',
        features: ['25 domains', '200 GB SSD storage', '100 email accounts', '50 databases', 'Priority support'],
        cta: { label: 'Order Pro', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Compare',
    heading: 'Compare the plans',
    intro: 'See what each plan includes.',
    featureLabel: 'Feature',
    yes: 'Included',
    no: 'Not included',
    rows: [
      { label: 'Capacity', values: [], group: true },
      { label: 'Domains', values: ['1', '5', '25'] },
      { label: 'SSD storage', values: ['10 GB', '50 GB', '200 GB'] },
      { label: 'Email accounts', values: ['5', '25', '100'] },
      { label: 'Databases', values: ['1', '10', '50'] },
      { label: 'Security and support', values: [], group: true },
      { label: 'SSL certificate', values: [true, true, true] },
      { label: 'Daily backup', values: [false, true, true] },
      { label: 'Priority support', values: [false, false, true] },
    ],
  },
  useCases: {
    eyebrow: 'Use cases',
    heading: 'How it is used',
    items: [
      { icon: 'domain', title: 'A website for your business', body: 'Publish a website on your own domain, with email and SSL.', link: { label: 'Request a quote', href: CONTACT } },
      { icon: 'database', title: 'CMS and web applications', body: 'Run a site or an application with its own databases.', link: { label: 'Request a quote', href: CONTACT } },
      { icon: 'backup', title: 'Moving from another provider', body: 'We help you move websites, databases and email.', link: { label: 'Contact us', href: CONTACT } },
    ],
  },
  related: {
    eyebrow: 'Related',
    heading: 'Related services',
    items: [
      { icon: 'wordpress', name: 'inWordPress — WordPress hosting', href: '/wordpress' },
      { icon: 'nodejs', name: 'inNode — Node.js hosting', href: '/nodejs' },
      { icon: 'virtual', name: 'inVPS — virtual servers', href: '/vps' },
      { icon: 'globe', name: 'inDomain — domains', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      { question: 'Can I change plans later?', answer: 'Yes, you can move to a larger plan as your needs grow. Get in touch and we will help you.' },
      { question: 'Can I move my website here?', answer: 'Yes. We help you move websites, databases and email from your current provider.' },
      { question: 'Is an SSL certificate included?', answer: 'Yes, an SSL certificate is included in every plan.' },
      { question: 'How do I order?', answer: 'Send us an email with the plan you want, and we will get in touch to set up your hosting.' },
    ],
  },
  cta: {
    heading: 'Ready to get started?',
    body: 'Contact us and we will set up your hosting.',
    primary: { label: 'Contact us', href: CONTACT },
  },
  footer: {
    tagline: 'inHost — web hosting with operations, security and support for your website.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inHost',
  },
};

export const WEBHOTELL_CONTENT: Record<Locale, WebhotellContent> = { no, en };
